import { useRef, useState } from "react";
import { useDecideConversationGmailApprovalMutation, useGetConversationGmailApprovalsQuery, type ConversationScope } from "@/features/chat/conversationApi";
import { gmailApprovalError, gmailFailureReason, type GmailApproval } from "@/features/chat/gmailApprovalSchemas";
import { MerryButton } from "@/shared/ui/MerryButton";
import "@/features/chat/conversationGmailApprovals.css";

function approvalStatus(item: GmailApproval) {
  if (item.status === "succeeded") return "已发送";
  if (item.status === "failed") return "发送失败，尚未确认发送成功";
  if (item.status === "canceled") return "已取消发送";
  if (item.delivery_status === "retryable_failed") return "审批提交失败，尚未确认发送";
  if (item.delivery_status || item.status === "running" || item.status === "planned") {
    return item.decision === "rejected" ? "取消请求已受理，等待结果" : "发送请求已受理，等待发送结果";
  }
  return "尚未发送，请确认邮件内容";
}

function GmailApprovalCard({ approval, scope, refresh }: { approval: GmailApproval; scope: ConversationScope; refresh: () => Promise<unknown> }) {
  const [decide] = useDecideConversationGmailApprovalMutation();
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const [accepted, setAccepted] = useState(false);
  // 服务端明确派发失败后允许同键重试；本地受理锁不能永久覆盖权威回读。
  const acceptedLock = accepted && approval.delivery_status !== "retryable_failed";
  const submit = async (decision: "approved" | "rejected") => {
    if (inFlight.current || acceptedLock) return;
    inFlight.current = true; setSubmitting(true); setNotice("");
    try {
      const result = await decide({ ...scope, approval, decision }).unwrap();
      setAccepted(result.status !== "retryable_failed");
      setNotice(result.status === "retryable_failed" ? gmailApprovalError("approval_dispatch_failed") : decision === "approved" ? "请求已受理，正在等待发送结果；尚未确认已发送。" : "取消请求已受理，正在等待结果。");
    } catch {
      // 网络中断可能发生在受理之后，先回读；同一版本始终复用相同幂等键。
      setNotice("提交结果暂时无法确认，请读取最新状态后重试；不会自动重复发送。");
    } finally {
      await refresh(); inFlight.current = false; setSubmitting(false);
    }
  };
  const terminal = ["succeeded", "failed", "canceled"].includes(approval.status);
  return (
    <article className="merry-gmail-approval" data-testid="gmail-approval-card" aria-label="邮件发送确认">
      <h3>邮件发送预览</h3>
      <p role="status">{submitting ? "正在提交确认…" : approvalStatus(approval)}</p>
      <dl>
        <dt>发件人</dt><dd>{approval.from || "暂无法确认"}</dd>
        <dt>收件人</dt><dd>{approval.to.join("、") || "尚未提供"}</dd>
        {approval.cc.length ? <><dt>抄送</dt><dd>{approval.cc.join("、")}</dd></> : null}
        {approval.bcc.length ? <><dt>密送</dt><dd>{approval.bcc.join("、")}</dd></> : null}
        <dt>主题</dt><dd>{approval.subject || "暂无法读取"}</dd>
      </dl>
      <div className="merry-gmail-approval__body"><span>正文</span><pre>{approval.text || "暂无法完整读取邮件正文"}</pre></div>
      {approval.follow_up ? <p>发送成功后将按方案观察邮件回复。</p> : null}
      {approval.status === "failed" ? <p role="alert">{gmailFailureReason(approval)}</p> : null}
      {!terminal && approval.blocking_code ? <p role="alert">{gmailApprovalError(approval.blocking_code)}</p> : null}
      {notice && !terminal ? <p role="status">{notice}</p> : null}
      <div className="merry-gmail-approval__actions">
        {!terminal && (!approval.delivery_status || approval.delivery_status === "retryable_failed") ? <>
          <MerryButton size={34} isDisabled={!approval.can_confirm || submitting || acceptedLock} onPress={() => void submit("approved")}>确认并发送</MerryButton>
          <MerryButton size={34} hierarchy="level1" isDisabled={!approval.can_cancel || submitting || acceptedLock} onPress={() => void submit("rejected")}>取消发送</MerryButton>
        </> : null}
        <MerryButton size={34} hierarchy="level2" isDisabled={submitting} onPress={() => void refresh()}>读取最新状态</MerryButton>
      </div>
    </article>
  );
}

export function ConversationGmailApprovals({ scope }: { scope: ConversationScope }) {
  const query = useGetConversationGmailApprovalsQuery(scope, { pollingInterval: 5000, skipPollingIfUnfocused: true });
  const items = query.currentData?.approvals ?? [];
  if (query.isError) return <section className="merry-gmail-approval" role="alert">邮件审批状态读取失败，尚未确认发送。<MerryButton size={34} onPress={() => void query.refetch()}>重新读取邮件审批</MerryButton></section>;
  if (!items.length) return null;
  return <section className="merry-gmail-approvals" aria-label="邮件审批">{items.map(item => <GmailApprovalCard key={`${scope.userId}:${scope.organizationId}:${scope.conversationId}:${item.execution_id}:${item.plan_hash}:${item.state_revision}`} approval={item} scope={scope} refresh={() => query.refetch()} />)}</section>;
}
